import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { animate } from 'framer-motion'
import { getLevelColors } from '../data/flavorMeta'
import type { FlavorLevel } from '../data'

export interface WheelPairing {
  slug: string
  name: string
  level: FlavorLevel | null
}

interface FlavorWheelProps {
  pairings: WheelPairing[]
  /** Центр колеса: имя выбранного вкуса и подпись под ним */
  centerName: string
  centerSub: string
  onActiveChange: (index: number) => void
}

// Геометрия колеса (координаты viewBox, центр в 0,0)
const R_OUT = 150
const R_IN = 95
const R_LABEL = (R_OUT + R_IN) / 2
const GAP_DEG = 1

const rad = (deg: number) => (deg * Math.PI) / 180
const pt = (r: number, deg: number) =>
  `${(r * Math.cos(rad(deg))).toFixed(2)} ${(r * Math.sin(rad(deg))).toFixed(2)}`

/** Донат-сектор от угла a0 до a1 (градусы, по часовой). */
const sectorPath = (a0: number, a1: number) => {
  const large = a1 - a0 > 180 ? 1 : 0
  return [
    `M ${pt(R_OUT, a0)}`,
    `A ${R_OUT} ${R_OUT} 0 ${large} 1 ${pt(R_OUT, a1)}`,
    `L ${pt(R_IN, a1)}`,
    `A ${R_IN} ${R_IN} 0 ${large} 0 ${pt(R_IN, a0)}`,
    'Z',
  ].join(' ')
}

/** Нормализация угла в диапазон (-180, 180]. */
const normDelta = (d: number) => ((d % 360) + 540) % 360 - 180

/** Индекс сегмента у указателя (12 часов) при данном повороте. */
const activeFromRot = (rot: number, n: number) => {
  const i = Math.round(-rot / (360 / n)) % n
  return (i + n) % n
}

const labelFontSize = (n: number) => {
  if (n <= 12) return 12.5
  if (n <= 20) return 11
  if (n <= 26) return 10
  return 9
}

/** Обрезка длинных имён под радиальную подпись. */
const wheelLabel = (name: string, n: number) => {
  const max = n <= 16 ? 16 : 13
  return name.length > max ? name.slice(0, max - 1).trimEnd() + '…' : name
}

/**
 * Интерактивное колесо сочетаний: сегменты — вкусы-пары, окрашенные по уровню
 * смелости. Колесо крутится свайпом, сегмент под указателем сверху — активный.
 * Тап по сегменту доворачивает его к указателю.
 */
const FlavorWheel = ({
  pairings,
  centerName,
  centerSub,
  onActiveChange,
}: FlavorWheelProps) => {
  const n = pairings.length
  const step = 360 / n

  const [rot, setRot] = useState(0)
  const rotRef = useRef(0)
  const svgRef = useRef<SVGSVGElement>(null)
  const centerRef = useRef({ x: 0, y: 0 })
  const lastAngleRef = useRef(0)
  const movedRef = useRef(0)
  const draggingRef = useRef(false)
  const animRef = useRef<ReturnType<typeof animate> | null>(null)
  const activeRef = useRef(0)

  const setRotation = useCallback(
    (value: number) => {
      rotRef.current = value
      setRot(value)
      const active = activeFromRot(value, n)
      if (active !== activeRef.current) {
        activeRef.current = active
        onActiveChange(active)
        // Лёгкий отклик при перещёлкивании сегмента в Telegram
        window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.()
      }
    },
    [n, onActiveChange],
  )

  // Вступительный доворот — колесо «прикатывается» на место
  useEffect(() => {
    animRef.current = animate(-step * 2, 0, {
      type: 'spring',
      stiffness: 60,
      damping: 14,
      onUpdate: v => setRotation(v),
    })
    return () => animRef.current?.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const snapTo = useCallback(
    (target: number) => {
      animRef.current?.stop()
      animRef.current = animate(rotRef.current, target, {
        type: 'spring',
        stiffness: 260,
        damping: 26,
        onUpdate: v => setRotation(v),
      })
    },
    [setRotation],
  )

  const pointerAngle = (e: PointerEvent | React.PointerEvent) => {
    const { x, y } = centerRef.current
    return (Math.atan2(e.clientY - y, e.clientX - x) * 180) / Math.PI
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    animRef.current?.stop()
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    centerRef.current = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    }
    draggingRef.current = true
    movedRef.current = 0
    lastAngleRef.current = pointerAngle(e)

    const onMove = (ev: PointerEvent) => {
      if (!draggingRef.current) return
      const a = pointerAngle(ev)
      const d = normDelta(a - lastAngleRef.current)
      lastAngleRef.current = a
      movedRef.current += Math.abs(d)
      setRotation(rotRef.current + d)
    }
    const onUp = () => {
      draggingRef.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      // Тап (без вращения) обрабатывает клик по сегменту, докрутку не делаем
      if (movedRef.current > 3) {
        snapTo(Math.round(rotRef.current / step) * step)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const rotateSegmentToTop = (index: number) => {
    if (movedRef.current > 3) return // это был драг, не тап
    const base = -index * step
    const k = Math.round((rotRef.current - base) / 360)
    snapTo(base + k * 360)
  }

  const activeIndex = activeFromRot(rot, n)
  const fontSize = labelFontSize(n)

  const segments = useMemo(
    () =>
      pairings.map((p, i) => {
        const center = i * step - 90
        return {
          ...p,
          index: i,
          center,
          path: sectorPath(
            center - step / 2 + GAP_DEG / 2,
            center + step / 2 - GAP_DEG / 2,
          ),
          labelX: R_LABEL * Math.cos(rad(center)),
          labelY: R_LABEL * Math.sin(rad(center)),
        }
      }),
    [pairings, step],
  )

  return (
    <div className="flavor-wheel">
      <svg
        ref={svgRef}
        className="flavor-wheel__svg"
        viewBox="-170 -170 340 340"
        onPointerDown={handlePointerDown}
      >
        <g transform={`rotate(${rot})`}>
          {segments.map(seg => {
            const colors = getLevelColors(seg.level)
            const isActive = seg.index === activeIndex
            // Подписи в левой полусфере переворачиваем, чтобы читались слева направо
            const absAngle = (((seg.center + rot) % 360) + 360) % 360
            const flipped = absAngle > 90 && absAngle < 270
            return (
              <g
                key={seg.slug}
                className="flavor-wheel__segment"
                onClick={() => rotateSegmentToTop(seg.index)}
              >
                <path
                  d={seg.path}
                  fill={isActive ? colors.main : colors.soft}
                />
                <text
                  x={seg.labelX}
                  y={seg.labelY}
                  fontSize={fontSize}
                  fill={isActive ? '#ffffff' : colors.text}
                  fontWeight={isActive ? 700 : 500}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${seg.center + (flipped ? 180 : 0)}, ${seg.labelX}, ${seg.labelY})`}
                >
                  {wheelLabel(seg.name, n)}
                </text>
              </g>
            )
          })}
        </g>
        {/* Указатель активного сегмента */}
        <path
          className="flavor-wheel__pointer"
          d="M -9 -168 L 9 -168 L 0 -150 Z"
        />
      </svg>

      <div className="flavor-wheel__center">
        <span className="flavor-wheel__center-name">{centerName}</span>
        <span className="flavor-wheel__center-sub">{centerSub}</span>
      </div>
    </div>
  )
}

export default FlavorWheel
